(() => {
  'use strict';
  const ELEMENT_NAME='s-anesthetic-risk-estimator';
  if(customElements.get(ELEMENT_NAME)) return;
  class AnestheticRiskEstimator extends HTMLElement{
    connectedCallback(){
      if(this.dataset.initialized==='true') return;
      this.dataset.initialized='true';
      const root=this;
      let lastResult=null;
      /* =====================================================================
         ASK A VET(TM) Anesthetic Risk Estimator
         -----------------------------------------------------------------
         Educational planning tool. Produces:
           - a non-validated 1-10 discussion score (owner-facing)
           - a provisional ASA physical-status discussion prompt 1-5 (+E) (clinical)
           - published perioperative mortality context (clinical)
         Model logic (transparent, not a black box):
           patientBurden = age + body condition + cardiac stage + kidney
              + current stability + other conditions   (health severity)
           -> maps to ASA physical status 1-5
           score10 = base + patientBurden weighting + species baseline
              + urgency + invasiveness + medication complexity, clamped 1-10
         Evidence anchors for the mortality context table:
           CEPSAF (Brodbelt 2008, Vet Anaesth Analg): dogs ~0.17% overall
              (healthy ~0.05%, sick ASA3-5 ~1.33%); cats ~0.24% overall
              (healthy ~0.11%, sick ~1.4%); rabbits ~1.39% overall
              (healthy ~0.73%, sick ~7.37%).
           Guinea pig perioperative mortality ~3.8% (Brodbelt).
           CEPEF4 (equine, 2025): overall ~1.2%, non-colic ~0.6%,
              colic ~4.2% within seven days.
         THIS IS A TEACHING ESTIMATE, NOT A DIAGNOSIS OR A GUARANTEE.
         ===================================================================== */

      /* ---------- Species config ---------- */
      var SPECIES = {
        dog:      {label:'Dog',        ic:'🐶', cardiac:true,  size:true,  base:0.0,
                   ages:{ped:0.5, adult:2, mature:7, senior:9, geri:12}},
        cat:      {label:'Cat',        ic:'🐱', cardiac:true,  size:false, base:0.1,
                   ages:{ped:0.5, adult:2, mature:7, senior:11, geri:15}},
        ferret:   {label:'Ferret',     ic:'🦦', cardiac:true,  size:false, base:0.4,
                   ages:{ped:0.4, adult:1, mature:3, senior:4, geri:6}},
        rabbit:   {label:'Rabbit',     ic:'🐰', cardiac:false, size:false, base:1.3,
                   ages:{ped:0.4, adult:1, mature:5, senior:7, geri:9}},
        guineapig:{label:'Guinea pig', ic:'🐹', cardiac:false, size:false, base:1.6,
                   ages:{ped:0.3, adult:0.6, mature:3, senior:4, geri:6}},
        bird:     {label:'Bird',       ic:'🦜', cardiac:false, size:false, base:1.5,
                   ages:{ped:0.4, adult:1, mature:8, senior:15, geri:25}},
        horse:    {label:'Horse',      ic:'🐎', cardiac:false, size:false, base:1.2,
                   ages:{ped:1, adult:4, mature:15, senior:20, geri:25}},
        cattle:   {label:'Cattle',     ic:'🐄', cardiac:false, size:false, base:0.8,
                   ages:{ped:0.5, adult:2, mature:8, senior:11, geri:14}},
        sheep:    {label:'Sheep',      ic:'🐑', cardiac:false, size:false, base:0.7,
                   ages:{ped:0.5, adult:1.5, mature:7, senior:9, geri:12}},
        goat:     {label:'Goat',       ic:'🐐', cardiac:false, size:false, base:0.7,
                   ages:{ped:0.5, adult:1.5, mature:8, senior:10, geri:13}},
        pig:      {label:'Pig',        ic:'🐷', cardiac:false, size:false, base:0.9,
                   ages:{ped:0.4, adult:1, mature:6, senior:9, geri:12}}
      };

      /* ---------- Conditions (checkboxes). species: 'all' or array ---------- */
      var CONDITIONS = [
        {id:'resp',    label:'Respiratory / lung disease', sm:'Pneumonia, effusion, collapsing airway, severe kennel cough', pts:4, species:'all'},
        {id:'brachy',  label:'Brachycephalic (flat-faced)', sm:'Pug, Frenchie, Bulldog, Boston, Persian, Himalayan', pts:2, species:['dog','cat']},
        {id:'asthma',  label:'Feline asthma / airway disease', sm:'Known lower-airway disease', pts:2, species:['cat']},
        {id:'laryngeal',label:'Laryngeal paralysis / GOLPP', sm:'Noisy breathing, voice change, older Labs', pts:3, species:['dog']},
        {id:'liver',   label:'Liver disease', sm:'Raised liver values, shunt, jaundice', pts:3, species:'all'},
        {id:'endocrine',label:'Diabetes or endocrine disease', sm:'Diabetes, Cushing’s, Addison’s, thyroid disease', pts:3, species:'all'},
        {id:'anaemia', label:'Anaemia or bleeding disorder', sm:'Low red cells, or a known clotting problem', pts:4, species:'all'},
        {id:'seizure', label:'Seizures / neurological disease', sm:'Epilepsy or other neuro condition', pts:1, species:'all'},
        {id:'cancer',  label:'Cancer / on chemotherapy', sm:'Active cancer or chemo drugs', pts:2, species:'all'},
        {id:'pregnant',label:'Pregnant or in labour', sm:'Including caesarean', pts:2, species:'all'},
        {id:'gdv',     label:'Bloat / GDV or gut obstruction', sm:'A surgical emergency — tell your vet now', pts:5, species:['dog','cat']},
        {id:'stasis',  label:'Gut stasis / not eating', sm:'A true emergency in these species', pts:4, species:['rabbit','guineapig']},
        {id:'colic',   label:'Colic', sm:'Colic surgery carries much higher anaesthetic risk', pts:5, species:['horse']},
        {id:'ruminalbloat',label:'Bloat / rumen tympany', sm:'Risk of regurgitation and pressure on the lungs', pts:4, species:['cattle','sheep','goat']}
      ];

      /* ---------- Cardiac staging options per disease ---------- */
      var STAGES = {
        mmvd:{label:'MMVD stage (ACVIM)', hint:'Stage B1 = murmur, heart still normal size. B2 = heart enlarging. C = has been in heart failure. D = failure that’s hard to control.',
          opts:[
            ['b1','Stage B1 — murmur, heart not enlarged',3],
            ['b2','Stage B2 — heart enlarging, not yet in failure',4],
            ['c','Stage C — in or previously in heart failure',7],
            ['d','Stage D — advanced / hard-to-control failure',9]
          ]},
        dcm:{label:'DCM stage', hint:'Occult = detected on scan, no signs yet. Overt = coughing, breathless, or has been in heart failure.',
          opts:[
            ['occult','Occult — found on scan, no symptoms',4],
            ['overt','Overt — in or previously in heart failure',7]
          ]},
        hcm:{label:'HCM stage', hint:'Subclinical = murmur or scan change, no signs. With CHF/ATE = has had fluid on the lungs or a clot (saddle thrombus).',
          opts:[
            ['sub','Subclinical — no symptoms',4],
            ['chf','With heart failure or a past clot (ATE)',8]
          ]},
        other:{label:'Severity', hint:'Your best sense of how significant your vet considers it.',
          opts:[
            ['mild','Mild / well compensated',3],
            ['severe','Significant / causing symptoms',7]
          ]}
      };

      var MUR_PTS = {soft:1, mod:2, loud:3, unknown:2};
      var BCS_PTS = {ideal:0, over:1, obese:2, under:2};
      var KID_PTS = {none:0, mild:2, mod:4, severe:6};
      var STAB_PTS= {stable:0, mild:1, unstable:4, critical:7};
      var MED_PTS = {none:0, routine:0, highrisk:1};
      var URG_PTS = {elective:0, urgent:0.8, emergency:2.0};
      var INV_PTS = {minor:-0.4, moderate:0.3, major:1.2, extreme:2.0};

      /* ---------- Breed predispositions ----------
         Flags surface as advisories + modest, non-double-counted nudges.
         Confirmed heart disease is owned by the Heart dropdown; a breed flag
         only prompts screening and (if no disease is already selected) a small
         nudge, so a Cavalier with confirmed MMVD isn't penalised twice. */
      var BREED_FLAG = {
        brachy:    {pts:2,   watch:'<b>Brachycephalic airway.</b> Flat-faced breeds desaturate fast and can obstruct on induction and in recovery. Pre-oxygenate, keep intubated as long as possible, and watch them closely until fully awake and sitting up.', tone:'md'},
        sighthound:{pts:1,   watch:'<b>Sighthound drug sensitivity.</b> Lean body fat and slower clearance of some agents (classically thiopental) mean prolonged, sometimes rough recoveries. Dose to lean body weight and pick short-acting drugs.', tone:'md'},
        mdr1:      {pts:0.5, watch:'<b>MDR1 / ABCB1 gene risk.</b> Herding breeds can be sensitive to certain sedatives and pre-meds (e.g. acepromazine, butorphanol). Ask about MDR1 testing and reduced dosing.', tone:'md'},
        dcm:       {pts:0,   nudge:0.5, watch:'<b>DCM-prone breed.</b> Dilated cardiomyopathy can be silent until a crisis. Ask about screening (echo &plusmn; Holter) before an elective anaesthetic if it hasn’t been done.', tone:'md'},
        mmvd:      {pts:0,   nudge:0.5, watch:'<b>MMVD-prone breed.</b> Degenerative valve disease is common in this breed. Make sure the heart is auscultated and any murmur investigated before the day.', tone:'lo'},
        hcm:       {pts:0,   nudge:0.5, watch:'<b>HCM-prone breed.</b> Hypertrophic cardiomyopathy is frequently silent in these cats and changes fluid and drug choices. Ask whether an echo (or proBNP screen) is warranted first.', tone:'md'},
        laryngeal: {pts:0,   watch:'<b>Laryngeal paralysis risk.</b> Older large breeds with noisy breathing or a voice change need airway care and a smooth recovery. Mention any change in bark or breathing.', tone:'lo'},
        giant:     {pts:0.5, watch:'<b>Giant breed.</b> Size means careful positioning, dosing to lean weight, and extra attention to blood pressure and recovery.', tone:'lo'},
        bloat:     {pts:0,   watch:'<b>Bloat-prone (deep-chested).</b> Not an anaesthetic risk in itself, but discuss whether a preventive gastropexy is worth adding if the abdomen is already open.', tone:'lo'},
        pkd:       {pts:0,   watch:'<b>Kidney-disease-prone breed.</b> Ask for pre-anaesthetic kidney bloods; some lines carry polycystic kidney disease that changes fluid planning.', tone:'lo'}
      };
      /* [name, sizeBand, [flags]] — sizeBand auto-sets the dog size field */
      var DOG_BREEDS = [
        ['Mixed breed / not listed','auto',[]],
        ['French Bulldog','small',['brachy']],
        ['Pug','small',['brachy']],
        ['English / British Bulldog','medium',['brachy']],
        ['Boston Terrier','small',['brachy']],
        ['Boxer','large',['brachy','dcm']],
        ['Shih Tzu','small',['brachy']],
        ['Pekingese','small',['brachy']],
        ['Cavalier King Charles Spaniel','small',['mmvd']],
        ['Dachshund','small',['mmvd']],
        ['Chihuahua','small',['mmvd']],
        ['Doberman','large',['dcm']],
        ['Great Dane','giant',['dcm','giant','bloat']],
        ['Irish Wolfhound','giant',['dcm','giant']],
        ['Newfoundland','giant',['dcm','giant']],
        ['Cocker Spaniel','medium',['dcm','mmvd']],
        ['Labrador Retriever','large',['laryngeal']],
        ['Golden Retriever','large',[]],
        ['German Shepherd','large',['bloat']],
        ['Weimaraner','large',['bloat']],
        ['Standard Poodle','large',['bloat']],
        ['Border Collie','medium',['mdr1']],
        ['Rough Collie','medium',['mdr1']],
        ['Australian Shepherd','medium',['mdr1']],
        ['Shetland Sheepdog','small',['mdr1']],
        ['Greyhound','large',['sighthound']],
        ['Whippet','medium',['sighthound']],
        ['Italian Greyhound','small',['sighthound']],
        ['Saluki / Borzoi','large',['sighthound']]
      ];
      var CAT_BREEDS = [
        ['Domestic / mixed','auto',[]],
        ['Maine Coon','auto',['hcm']],
        ['Ragdoll','auto',['hcm']],
        ['British Shorthair','auto',['hcm']],
        ['Sphynx','auto',['hcm']],
        ['Bengal','auto',['hcm']],
        ['Persian','auto',['brachy','pkd']],
        ['Himalayan','auto',['brachy','pkd']],
        ['Exotic Shorthair','auto',['brachy']],
        ['Siamese','auto',[]],
        ['Devon Rex','auto',[]]
      ];

      /* ---------- state ---------- */
      var state = {species:'dog', mode:'owner'};

      /* ---------- build UI ---------- */
      function buildSpecies(){
        var g=root.querySelector("#speciesGrid"); g.innerHTML='';
        Object.keys(SPECIES).forEach(function(k){
          var s=SPECIES[k];
          var d=document.createElement('button');
          d.type='button'; d.className='sp'+(k===state.species?' on':''); d.dataset.k=k;
          d.setAttribute('aria-pressed', k===state.species ? 'true' : 'false');
          d.setAttribute('aria-label', s.label);
          d.innerHTML='<span class="ic" aria-hidden="true">'+s.ic+'</span><span class="nm">'+s.label+'</span>';
          d.onclick=function(){ if(k===state.species) return; state.species=k; buildSpecies(); applySpecies(); };
          g.appendChild(d);
        });
      }
      function applySpecies(){
        var s=SPECIES[state.species];
        root.querySelector("#dogSizeField").style.display = s.size?'':'none';
        root.querySelector("#heartCard").style.display = s.cardiac?'':'none';
        /* keep visible step numbers sequential when the heart card is hidden */
        root.querySelector("#stepHealth").textContent = s.cardiac ? '4' : '3';
        root.querySelector("#stepProcedure").textContent = s.cardiac ? '5' : '4';
        buildBreeds();
        buildConditions();
        onCardiacChange();
      }
      function breedListFor(){
        if(state.species==='dog') return DOG_BREEDS;
        if(state.species==='cat') return CAT_BREEDS;
        return null;
      }
      function buildBreeds(){
        var f=root.querySelector("#breedField");
        var list=breedListFor();
        if(!list){ f.style.display='none'; return; }
        f.style.display='';
        var sel=root.querySelector("#breed"); sel.innerHTML='';
        list.forEach(function(b,i){
          var op=document.createElement('option'); op.value=i; op.textContent=b[0]; sel.appendChild(op);
        });
        sel.value=0; onBreedChange();
      }
      function currentBreed(){
        var list=breedListFor(); if(!list) return null;
        var i=parseInt(root.querySelector("#breed").value,10)||0;
        return list[i];
      }
      function onBreedChange(){
        var b=currentBreed(); if(!b) return;
        // auto-set dog size band from breed
        if(state.species==='dog' && b[1]!=='auto'){ root.querySelector("#size").value=b[1]; }
        var flags=b[2]||[];
        var line=root.querySelector("#breedFlagLine");
        if(flags.length){
          var names=flags.map(function(fl){return {brachy:'brachycephalic airway',sighthound:'drug sensitivity',mdr1:'MDR1 gene',dcm:'DCM screening',mmvd:'MMVD screening',hcm:'HCM screening',laryngeal:'laryngeal paralysis',giant:'giant-breed care',bloat:'bloat-prone',pkd:'kidney screening'}[fl];}).join(', ');
          line.innerHTML='Flags for this breed: <b>'+names+'</b>. These appear in the results and adjust the plan, not a diagnosis.';
        } else {
          line.textContent='No specific breed predispositions flagged for this breed.';
        }
      }
      function buildConditions(){
        var g=root.querySelector("#condGrid"); g.innerHTML='';
        CONDITIONS.forEach(function(c){
          if(c.species!=='all' && c.species.indexOf(state.species)<0) return;
          var id='cond_'+c.id;
          var l=document.createElement('label'); l.className='chk';
          l.innerHTML='<input type="checkbox" id="'+id+'" data-pts="'+c.pts+'"><span>'+c.label+
            '<span class="sm">'+c.sm+'</span></span>';
          var cb=l.querySelector('input');
          cb.onchange=function(){ l.classList.toggle('on',cb.checked); };
          g.appendChild(l);
        });
      }
      function onCardiacChange(){
        var v=root.querySelector("#cardiac").value;
        var gradeF=root.querySelector("#gradeField");
        var stageF=root.querySelector("#stageField");
        gradeF.classList.toggle('hide', v!=='murmur');
        var hasStage = (v==='mmvd'||v==='dcm'||v==='hcm'||v==='other');
        stageF.classList.toggle('hide', !hasStage);
        if(hasStage){
          var cfg=STAGES[v];
          var sel=root.querySelector("#hStage"); sel.innerHTML='';
          cfg.opts.forEach(function(o){
            var op=document.createElement('option'); op.value=o[0]; op.textContent=o[1]; op.dataset.pts=o[2];
            sel.appendChild(op);
          });
          root.querySelector("#stageLabel").textContent=cfg.label;
          root.querySelector("#stageHint").textContent=cfg.hint;
        }
      }

      /* ---------- mode ---------- */
      function setMode(m){
        state.mode=m;
        root.classList.toggle('clinical', m==='clinical');
        root.querySelector("#mOwner").setAttribute('aria-pressed', m==='owner' ? 'true' : 'false');
        root.querySelector("#mClin").setAttribute('aria-pressed', m==='clinical' ? 'true' : 'false');
        root.querySelector("#modeHint").textContent = m==='owner'
          ? 'Plain-language mode: an educational score, what it means, and what to ask your vet.'
          : 'Clinical mode: adds a provisional ASA discussion prompt and published population context.';
        if(root.querySelector("#result").style.display==='block') renderResult(lastResult);
      }

      /* ---------- helpers ---------- */
      function ageBandFor(species, age){
        var a=SPECIES[species].ages;
        if(age < a.ped)   return {pts:2, name:'Neonate / paediatric', key:'ped'};
        if(age < a.adult) return {pts:0.5, name:'Juvenile', key:'juv'};
        if(age < a.mature)return {pts:0, name:'Adult', key:'adult'};
        if(age < a.senior)return {pts:1, name:'Mature', key:'mature'};
        if(age < a.geri)  return {pts:2, name:'Senior', key:'senior'};
        return {pts:3, name:'Geriatric', key:'geri'};
      }
      function ageBand(age){ return ageBandFor(state.species, age); }
      function asaFromBurden(b, emergency){
        var cls = b<=0 ? 1 : b<=2 ? 2 : b<=6 ? 3 : b<=11 ? 4 : 5;
        return {n:cls, label:'ASA '+cls+(emergency?'E':''), roman:['','I','II','III','IV','V'][cls]};
      }
      var ASA_TEXT = {
        1:'Normal, healthy patient',
        2:'Mild systemic disease, well compensated',
        3:'Severe systemic disease that limits function',
        4:'Severe disease that is a constant threat to life',
        5:'Moribund; not expected to survive without the procedure'
      };
      function bandFor(score){
        /* thresholds unchanged; colours re-tokenised to the ASK A VET(TM) palette
           and darkened so white badge text meets WCAG AA contrast */
        if(score<3)   return {name:'Low',       cls:'green', c1:'#17714a', c2:'#125c3c'};
        if(score<5)   return {name:'Moderate',  cls:'amber', c1:'#8a6508', c2:'#6f5106'};
        if(score<7)   return {name:'Elevated',  cls:'amber', c1:'#a84b08', c2:'#8a3d06'};
        if(score<8.5) return {name:'High',      cls:'red',   c1:'#c0362c', c2:'#9c2b23'};
        return                {name:'Critical', cls:'red',   c1:'#8f1c16', c2:'#731611'};
      }

      /* ---------- core calc ---------- */
      function calc(){
        var err=root.querySelector("#err");
        var ageY=parseFloat(root.querySelector("#ageY").value);
        if(isNaN(ageY)||ageY<0){ err.textContent='Please enter the animal’s age in years.'; err.style.display='block'; return; }
        err.style.display='none';

        var sp=SPECIES[state.species];
        var drivers=[]; // {label, sm, pts, tone}
        var burden=0;

        // age
        var ab=ageBand(ageY);
        burden+=ab.pts;
        if(ab.pts>=2) drivers.push({label:ab.name+' age', sm:pretty(ageY)+' years old', tone:ab.pts>=2.5?'hi':'md'});
        else if(ab.pts>0) drivers.push({label:ab.name+' age', sm:pretty(ageY)+' years old', tone:'lo'});

        // body condition
        var bcs=root.querySelector("#bcs").value;
        burden+=BCS_PTS[bcs];
        if(BCS_PTS[bcs]>0){
          drivers.push({label: bcs==='under'?'Underweight / frail':(bcs==='obese'?'Obese':'Overweight'),
            sm: bcs==='under'?'Less reserve, harder to dose and keep warm':'Extra load on heart and lungs, harder drug dosing',
            tone: bcs==='obese'||bcs==='under'?'md':'lo'});
        }

        // stability
        var stab=root.querySelector("#stability").value;
        burden+=STAB_PTS[stab];
        if(STAB_PTS[stab]>0){
          drivers.push({label: stab==='critical'?'Critically unwell now':(stab==='unstable'?'Unstable right now':'Mildly unwell'),
            sm: stab==='stable'?'':'Stabilising first is the biggest single win',
            tone: stab==='critical'?'hi':(stab==='unstable'?'hi':'md')});
        }

        // cardiac
        var cardTone=null, cardPts=0, cardLabel='';
        if(sp.cardiac){
          var cv=root.querySelector("#cardiac").value;
          if(cv==='murmur'){
            var mg=root.querySelector("#murGrade").value;
            cardPts=MUR_PTS[mg];
            cardLabel='Uninvestigated heart murmur';
            drivers.push({label:cardLabel, sm:'Worth a scan before anaesthesia to know what’s behind it', tone: mg==='loud'?'md':'lo'});
          } else if(cv==='mmvd'||cv==='dcm'||cv==='hcm'||cv==='other'){
            var sel=root.querySelector("#hStage");
            var op=sel.options[sel.selectedIndex];
            cardPts=parseFloat(op.dataset.pts);
            var dn={mmvd:'MMVD',dcm:'DCM',hcm:'HCM',other:'Heart condition'}[cv];
            cardLabel=dn+' — '+op.textContent.split('—')[0].trim();
            drivers.push({label:dn+' ('+op.textContent.split('—')[0].trim()+')',
              sm: cardPts>=7?'Major driver — needs a tailored cardiac-safe plan':'Changes drug choice and monitoring',
              tone: cardPts>=7?'hi':(cardPts>=4?'md':'lo')});
          }
          burden+=cardPts;
        }

        // kidney
        var kid=root.querySelector("#kidney").value;
        burden+=KID_PTS[kid];
        if(KID_PTS[kid]>0){
          drivers.push({label:'Kidney disease ('+({mild:'mild',mod:'moderate',severe:'advanced'}[kid])+')',
            sm:'May require individualised fluid and blood-pressure support', tone: kid==='severe'?'hi':(kid==='mod'?'md':'lo')});
        }

        // conditions
        CONDITIONS.forEach(function(c){
          if(c.species!=='all' && c.species.indexOf(state.species)<0) return;
          var cb=document.getElementById('cond_'+c.id);
          if(cb && cb.checked){
            burden+=c.pts;
            drivers.push({label:c.label, sm:c.sm, tone: c.pts>=4?'hi':(c.pts>=2?'md':'lo')});
          }
        });

        // breed predispositions
        var watch=[]; var breedPts=0; var breedName='';
        var b=currentBreed();
        if(b && b[2] && b[2].length){
          breedName=b[0];
          var flags=b[2];
          var brachyChecked = root.querySelector("#cond_brachy") && root.querySelector("#cond_brachy").checked;
          var cardSelected = sp.cardiac && root.querySelector("#cardiac").value!=='none';
          flags.forEach(function(fl){
            var meta=BREED_FLAG[fl]; if(!meta) return;
            // airway: only count if the brachy checkbox didn't already
            if(fl==='brachy'){ if(!brachyChecked){ burden+=meta.pts; drivers.push({label:breedName+' — brachycephalic airway', sm:'Flat-faced breed: airway obstruction and fast desaturation risk', tone:'md'}); } watch.push(meta.watch); return; }
            // heart-screening flags: nudge + advisory only if no heart disease already selected
            if(fl==='dcm'||fl==='mmvd'||fl==='hcm'){
              if(!cardSelected){ burden+=(meta.nudge||0); watch.push(meta.watch); if((meta.nudge||0)>0) drivers.push({label:breedName+' — breed-linked heart risk', sm:'Predisposed to '+fl.toUpperCase()+'; screening advised before anaesthesia', tone:'md'}); }
              return;
            }
            // everything else: fixed nudge + advisory
            if(meta.pts>0){ burden+=meta.pts; drivers.push({label:breedName+' — '+({sighthound:'drug sensitivity',mdr1:'MDR1 gene',giant:'giant-breed care'}[fl]||fl), sm:'Breed-linked anaesthetic consideration', tone:meta.tone}); }
            watch.push(meta.watch);
          });
        }

        // meds
        var med=root.querySelector("#meds").value;
        var medPts=MED_PTS[med];
        if(med==='highrisk') drivers.push({label:'High-risk medications', sm:'Heart / insulin / seizure / chemo meds need timing around the day', tone:'md'});

        // procedure
        var urg=root.querySelector("#urgency").value;
        var inv=root.querySelector("#invasive").value;
        var emergency = urg==='emergency';
        if(URG_PTS[urg]>0) drivers.push({label: emergency?'Emergency procedure':'Urgent procedure',
          sm: emergency?'Little time to stabilise or investigate':'Limited time to optimise first', tone: emergency?'hi':'md'});
        if(inv==='major'||inv==='extreme') drivers.push({label:(inv==='extreme'?'Very major':'Major')+' procedure',
          sm:'Longer time under and more physiological stress', tone: inv==='extreme'?'hi':'md'});

        // ---- score ----
        var score = 0.9 + burden*0.5 + sp.base + URG_PTS[urg] + INV_PTS[inv] + medPts;
        score = Math.max(1, Math.min(10, score));
        var scoreR = Math.round(score*10)/10;

        var asa = asaFromBurden(burden, emergency);
        var band = bandFor(score);

        // sort drivers by tone weight desc
        var toneW={hi:3,md:2,lo:1};
        drivers.sort(function(a,b){return toneW[b.tone]-toneW[a.tone];});
        if(drivers.length===0) drivers.push({label:'Healthy patient, low-stress plan', sm:'No major risk factors flagged', tone:'lo'});

        var out={score:scoreR, band:band, asa:asa, burden:burden, drivers:drivers,
          species:state.species, emergency:emergency, urg:urg, inv:inv, stab:stab, age:ageY,
          watch:watch, breedName:breedName,
          dogSize: state.species==='dog' ? root.querySelector("#size").value : '',
          cardiac: sp.cardiac ? root.querySelector("#cardiac").value : 'none'};
        lastResult=out;
        renderResult(out);
      }
      function pretty(n){ return (Math.round(n*10)/10).toString(); }
      function escapeHtml(value){
        return String(value).replace(/[&<>"']/g,function(ch){
          return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch];
        });
      }

      /* ---------- render ---------- */
      function renderResult(o){
        if(!o) return;
        var sp=SPECIES[o.species];
        var name=sp.label;
        var hero=root.querySelector("#hero");
        root.querySelector("#heroVal").innerHTML=o.score+'<small> / 10</small>';
        root.querySelector("#meterPin").style.left=(((o.score-1)/9)*100)+'%';
        root.querySelector("#riskMeter").setAttribute('aria-valuenow', o.score);
        root.querySelector("#riskMeter").setAttribute('aria-valuetext', o.score+' out of 10, '+o.band.name+' discussion band');
        var badge=root.querySelector("#bandBadge");
        badge.textContent=o.band.name+' discussion band';
        badge.style.background=o.band.c1;
        badge.style.borderColor=o.band.c2;
        badge.style.color='#fff';
        root.querySelector("#heroLab").textContent='Educational anaesthetic planning score';

        // stat line
        var sl=root.querySelector("#statline"); sl.innerHTML='';
        function stat(k,v,sub){var d=document.createElement('div');d.className='stat';
          d.innerHTML='<div class="k">'+k+'</div><div class="v">'+v+(sub?' <small>'+sub+'</small>':'')+'</div>';sl.appendChild(d);}
        stat('Planning band', o.band.name, 'of 5 levels');
        if(state.mode==='clinical'){
          stat('ASA prompt', o.asa.label, ASA_TEXT[o.asa.n].split(';')[0].split(',')[0]);
          stat('Procedure', cap(o.urg), invName(o.inv));
        } else {
          stat('Procedure', cap(o.urg), invName(o.inv));
          stat('Patient', sp.label, ageBandName(o));
        }

        // headline callout
        var hc=root.querySelector("#headline");
        hc.className='callout '+(o.band.cls);
        hc.innerHTML=headlineText(o, name);

        // drivers
        var dl=root.querySelector("#driverList"); dl.innerHTML='';
        o.drivers.forEach(function(d){
          var li=document.createElement('li');
          var pill = d.tone==='hi'?'<span class="tagpill hi">Major</span>' : d.tone==='md'?'<span class="tagpill md">Moderate</span>':'<span class="tagpill lo">Minor</span>';
          li.innerHTML='<span class="fl">'+d.label+(d.sm?'<small>'+d.sm+'</small>':'')+'</span>'+pill;
          dl.appendChild(li);
        });

        // breed watch-list
        var bp=root.querySelector("#breedPanel");
        if(o.watch && o.watch.length){
          bp.style.display='';
          root.querySelector("#breedPanelTitle").textContent='Breed watch-list'+(o.breedName?' — '+o.breedName:'');
          var wl=root.querySelector("#watchList"); wl.innerHTML='';
          o.watch.forEach(function(w){var li=document.createElement('li');li.innerHTML=w;wl.appendChild(li);});
        } else { bp.style.display='none'; }

        // print header (clinic handout)
        var pm=root.querySelector("#printMeta");
        var d=new Date();
        var dstr=d.toLocaleDateString(undefined,{year:'numeric',month:'short',day:'numeric'});
        pm.innerHTML='<b>ASK A VET&trade; Anaesthetic Planning Summary</b> &nbsp;·&nbsp; '+
          escapeHtml(sp.label)+', '+pretty(o.age)+' yr'+(o.breedName?' ('+escapeHtml(o.breedName)+')':'')+
          (o.dogSize?' &nbsp;·&nbsp; '+cap(escapeHtml(o.dogSize))+' adult size':'')+
          ' &nbsp;·&nbsp; '+cap(o.urg)+' '+invName(o.inv)+
          ' &nbsp;·&nbsp; Generated '+dstr+
          '<br><span style="font-size:11px">Non-validated educational score to support the veterinarian’s assessment &mdash; not a mortality prediction, ASA assignment or clearance for anaesthesia. By Dr Duncan Houston.</span>';

        // recommendations
        var rl=root.querySelector("#recList"); rl.innerHTML='';
        recsFor(o).forEach(function(r){var li=document.createElement('li');li.innerHTML=r;rl.appendChild(li);});
        root.querySelector("#recTitle").textContent = state.mode==='clinical'
          ? 'Risk-reduction & anaesthetic plan pointers' : 'How to lower the risk before the day';

        // clinical context table
        buildCtx(o);

        // emergency / booking box
        var eb=root.querySelector("#emergencyBox");
        if(o.emergency || o.stab==='critical' || o.stab==='unstable'){
          eb.className='callout red';
          eb.innerHTML='<b>Time-critical.</b> This is flagged as an emergency or an unstable patient. Do not wait on a calculator &mdash; call your vet or the nearest emergency clinic now. Where it is safe to do so, stabilising first (fluids, oxygen, pain relief, correcting the problem) is what lowers the risk.';
          eb.style.display='';
        } else if(o.score>=7){
          eb.className='callout amber';
          eb.innerHTML='<b>Have the risk conversation.</b> At this level, ask your vet directly about referral to a practice with a dedicated anaesthetist or specialist monitoring, and whether the procedure can wait until the patient is more stable or better worked up.';
          eb.style.display='';
        } else { eb.style.display='none'; }

        // disclaimer
        root.querySelector("#disclaimer").innerHTML =
          '<b>Important limitation:</b> this score has not been clinically validated and does not calculate this animal’s probability of death. It is not a diagnosis, ASA assignment, clearance or guarantee. It cannot examine the patient or assess the day-of-procedure findings that shape the plan. A high score does not automatically make anaesthesia inappropriate, and a low score does not make it risk-free. Only the treating veterinarian can assess the animal, assign ASA physical status and obtain consent. If the animal is unwell now, contact a veterinarian or emergency clinic.';

        root.classList.add('has-result');
        root.querySelector("#result").style.display='block';
        root.querySelector("#result").focus({preventScroll:true});
        root.querySelector("#result").scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
      }

      function headlineText(o,name){
        name=escapeHtml(name);
        var b=o.band.name;
        if(b==='Low') return '<b>'+name+' has a low educational planning band.</b> No major factors were selected, but a low score is not anaesthetic clearance and risk is never zero.';
        if(b==='Moderate') return '<b>Moderate discussion band.</b> One or more factors may justify extra assessment or an adjusted plan. Review the list below with the treating veterinarian.';
        if(b==='Elevated') return '<b>Elevated discussion band.</b> The selected factors support an individualised plan, appropriate monitoring and review of anything that can be optimised before the procedure.';
        if(b==='High') return '<b>High discussion band.</b> This result supports a detailed risk&ndash;benefit conversation, careful stabilisation and consideration of whether additional expertise or facilities are appropriate.';
        return '<b>Critical discussion band.</b> The selected factors indicate a high-stakes decision that requires immediate, case-specific veterinary assessment. The calculator cannot decide whether or when the procedure should proceed.';
      }

      function recsFor(o){
        var r=[]; var sp=SPECIES[o.species];
        r.push('<b>Targeted pre-anaesthetic assessment:</b> testing is selected from the history, examination and procedure and may include blood tests, urinalysis, blood pressure, ECG or imaging.');
        if(sp.cardiac && o.cardiac!=='none'){
          if(o.cardiac==='murmur') r.push('<b>Assess the murmur:</b> depending on the examination, symptoms, species and procedure, the treating veterinarian may recommend tests such as echocardiography before an elective anaesthetic.');
          else r.push('<b>Cardiac-tailored plan:</b> review whether cardiac testing is current and choose drugs, fluid strategy and monitoring for the diagnosed condition. Ask whether referral or specialist input is warranted.');
        }
        if(o.stab==='unstable'||o.stab==='critical') r.push('<b>Stabilise where possible:</b> support may include oxygen, analgesia, circulation support and correction of the underlying problem before induction, guided by the treating team and procedure urgency.');
        r.push('<b>IV access and patient support:</b> discuss secure venous access, an individualised fluid plan, blood-pressure support and active temperature management.');
        r.push('<b>Monitoring that matches the risk:</b> pulse oximetry, ECG, blood pressure, capnography and a dedicated person watching the anaesthetic, not just the surgery.');
        if(o.species==='rabbit'||o.species==='guineapig') r.push('<b>Small-herbivore protocol:</b> follow species-specific instructions from an exotics-experienced team; rabbits and guinea pigs should not undergo prolonged fasting. Plan warmth, airway care and gastrointestinal support.');
        if(o.species==='horse') r.push('<b>Equine-specific:</b> controlled recovery (assisted or rope), padded surface and experienced team &mdash; recovery is where much of the equine risk sits.');
        if(o.species==='bird') r.push('<b>Avian-specific:</b> tiny margins for error &mdash; precise weighing, warmth, short procedure times and an experienced avian anaesthetist.');
        if(['cattle','sheep','goat','pig'].indexOf(o.species)>=0) r.push('<b>Ruminant / pig care:</b> correct fasting to reduce regurgitation and bloat, protect the airway, and watch positioning to avoid nerve and muscle injury.');
        if(o.score<3) r.push('<b>Keep it simple:</b> at this risk level the main job is a good pre-anaesthetic exam and not over-complicating a healthy patient.');
        return r;
      }

      /* published mortality context (clinical) */
      function buildCtx(o){
        var t=root.querySelector("#ctxTable");
        var rows=[];
        var s=o.species;
        if(s==='dog'){
          rows.push(['All dogs (historic CEPSAF)','~0.17%  (about 1 in 600)']);
          rows.push(['Healthy dogs (ASA 1&ndash;2)','~0.05%  (about 1 in 2,000)']);
          rows.push(['Sick dogs (ASA 3&ndash;5)','~1.3%  (about 1 in 75)']);
        } else if(s==='cat'){
          rows.push(['All cats (historic CEPSAF)','~0.24%  (about 1 in 420)']);
          rows.push(['Healthy cats (ASA 1&ndash;2)','~0.11%  (about 1 in 900)']);
          rows.push(['Sick cats (ASA 3&ndash;5)','~1.4%  (about 1 in 70)']);
        } else if(s==='rabbit'){
          rows.push(['All rabbits (historic CEPSAF)','~1.4%  (about 1 in 72)']);
          rows.push(['Healthy rabbits','~0.7%  (about 1 in 140)']);
          rows.push(['Sick rabbits','~7%  (about 1 in 14)']);
        } else if(s==='guineapig'){
          rows.push(['Guinea pigs (historic estimate)','~3.8%  (about 1 in 26)']);
          rows.push(['Interpretation','Limited cases; not an individual prediction']);
        } else if(s==='ferret'){
          rows.push(['Ferrets','Robust pooled mortality data are limited']);
        } else if(s==='bird'){
          rows.push(['Birds','Risk is highly species-, size- and procedure-dependent; pooled data are limited']);
        } else if(s==='horse'){
          rows.push(['All general anaesthetics (CEPEF4)','~1.2%  (about 1 in 83)']);
          rows.push(['Non-colic general anaesthesia','~0.6%  (about 1 in 167)']);
          rows.push(['Colic general anaesthesia','~4.2%  (about 1 in 24)']);
        } else {
          rows.push(['Farm species','Limited pooled data; regurgitation, bloat, positioning and recovery are the main risks']);
        }
        t.innerHTML='';
        var caption=document.createElement('caption');
        caption.className='sr-only';
        caption.textContent='Published population-level veterinary anaesthetic mortality figures';
        t.appendChild(caption);
        var thead=document.createElement('thead');
        var headRow=document.createElement('tr');
        headRow.innerHTML='<th scope="col">Study group</th><th scope="col">Approximate reported mortality</th>';
        thead.appendChild(headRow); t.appendChild(thead);
        var tbody=document.createElement('tbody');
        rows.forEach(function(r){
          var tr=document.createElement('tr');
          tr.innerHTML='<td>'+r[0]+'</td><td>'+r[1]+'</td>';
          tbody.appendChild(tr);
        });
        t.appendChild(tbody);
        var src = (s==='horse')
          ? 'Source: <a href="https://pubmed.ncbi.nlm.nih.gov/40695694/" target="_blank" rel="noopener noreferrer">CEPEF4 (2025)</a>. Death within seven days after general anaesthesia; population context, not this patient’s odds.'
          : (['dog','cat','rabbit','guineapig'].indexOf(s)>=0)
            ? 'Source: <a href="https://pubmed.ncbi.nlm.nih.gov/18466167/" target="_blank" rel="noopener noreferrer">CEPSAF (Brodbelt et al., 2008)</a>. UK cases collected in 2002&ndash;2004; anaesthetic/sedation-related death within 48 hours. Historical population context, not this patient’s odds.'
            : 'Robust population mortality data are limited for this species. Use the result as a discussion guide only, not a numerical prognosis.';
        root.querySelector("#ctxSource").innerHTML=src;
      }

      /* small text helpers */
      function cap(s){return s.charAt(0).toUpperCase()+s.slice(1);}
      function invName(v){return {minor:'minor',moderate:'moderate',major:'major',extreme:'very major'}[v]+' procedure';}
      function ageBandName(o){ // recompute name for stat
        return ageBandFor(o.species,o.age).name;
      }

      /* ---------- reset ---------- */
      function resetAll(){
        state.species='dog';
        buildSpecies();
        applySpecies();               /* rebuilds breed list + condition tick boxes unchecked */
        var defaults={ageY:'',bcs:'ideal',stability:'stable',kidney:'none',meds:'none',
                      urgency:'elective',invasive:'moderate',size:'medium',
                      cardiac:'none',murGrade:'soft'};
        Object.keys(defaults).forEach(function(id){
          var el=document.getElementById(id);
          if(el) el.value=defaults[id];
        });
        onCardiacChange();
        var err=root.querySelector("#err");
        err.textContent=''; err.style.display='none';
        lastResult=null;
        root.classList.remove('has-result');
        var r=root.querySelector("#result");
        r.style.display='none';
        root.querySelector("#calculator").scrollIntoView({
          behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',
          block:'start'
        });
        root.querySelector("#ageY").focus({preventScroll:true});
      }

      /* ---------- init ---------- */
      buildSpecies();
      applySpecies();

      const mOwner=root.querySelector('#mOwner');
      const mClin=root.querySelector('#mClin');
      const breed=root.querySelector('#breed');
      const cardiac=root.querySelector('#cardiac');
      const calcBtn=root.querySelector('#calculator .actionrow .btn');
      const resetBtn=root.querySelector('#resetBtn');
      const printBtn=root.querySelector('#resultActions .btn-ghost');
      if(mOwner) mOwner.addEventListener('click',()=>setMode('owner'));
      if(mClin) mClin.addEventListener('click',()=>setMode('clinical'));
      if(breed) breed.addEventListener('change',onBreedChange);
      if(cardiac) cardiac.addEventListener('change',onCardiacChange);
      if(calcBtn) calcBtn.addEventListener('click',calc);
      if(resetBtn) resetBtn.addEventListener('click',resetAll);
      if(printBtn) printBtn.addEventListener('click',()=>window.print());
    }
  }
  customElements.define(ELEMENT_NAME,AnestheticRiskEstimator);
})();
